import { Navigate } from 'react-router-dom';
import { useCapabilities } from '../Capabilities';
import { DashboardPage } from './DashboardPage';

/**
 * What `/` shows, which is not the same page on every instance.
 *
 * The Dashboard is a *knowledge* overview and needs `knowledge.base`. On
 * `wingd`, which enables none, the sidebar correctly hides it — and `/` is
 * still where login lands, so hiding it alone left the first thing after
 * signing in as the one page that could not work.
 *
 * So the landing is chosen from what the instance has. `Overview` is the
 * answer for anything else, because it reads only endpoints the framework
 * itself serves and therefore cannot be unavailable — which makes the
 * fallback chain below a preference rather than a search for something
 * that works.
 *
 * The chain is left in place because an instance whose subject *is* work
 * is better served landing on it than on a generic overview; `Overview`
 * catches everything the chain does not.
 */
const FALLBACKS: Array<{ to: string; requires: string }> = [
  { to: '/team', requires: 'work.trends' },
  { to: '/team?tab=workload', requires: 'work.metrics' },
];

export function LandingRoute() {
  const caps = useCapabilities();

  // Unknown yet. Render nothing rather than flashing the Dashboard and
  // then navigating away from it.
  if (caps === null) return null;

  if (caps['knowledge.base'] === true) return <DashboardPage />;

  for (const option of FALLBACKS) {
    if (caps[option.requires as keyof typeof caps] === true) {
      return <Navigate to={option.to} replace />;
    }
  }
  // Nothing gated is available, so the framework's own overview is the
  // landing. It was the Dashboard's empty state, which explained that
  // knowledge is not enabled and nothing else — true, and useless on an
  // instance that never wanted knowledge.
  return <Navigate to="/overview" replace />;
}
